import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CycleDetail from "./CycleDetail";

export const REVIEW_STATUS_CHIP = {
  pending: "border border-slate-400 text-slate-600",
  ai_draft: "border border-teal-600 text-teal-700",
  manager_review: "bg-amber-500 text-white",
  finalized: "bg-teal-600 text-white",
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    {children}
  </div>
);

export const CyclesPanel = () => {
  const { user } = useAuth();
  const [cycles, setCycles] = useState(null);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", period_start: "", period_end: "", scope: "all", department: null });
  const [departments, setDepartments] = useState([]);
  const [creating, setCreating] = useState(false);
  const canCreate = ["admin", "hr_manager"].includes(user.role);

  const load = () => api.get("/reviews/cycles").then((r) => setCycles(r.data.cycles));
  useEffect(() => {
    load();
    api.get("/employees/departments").then((r) => setDepartments(r.data.departments)).catch(() => {});
  }, []);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/reviews/cycles", form);
      toast.success(`Cycle "${r.data.name}" opened — ${r.data.feedback_assignments} peer feedback requests sent`);
      setOpen(false);
      await load();
      setSelected(r.data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  if (selected) return <CycleDetail cycleId={selected} onBack={() => { setSelected(null); load(); }} />;
  if (!cycles) return <Skeleton className="h-80 w-full rounded-none" />;

  return (
    <div className="space-y-4" data-testid="cycles-panel">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-muted-foreground">
          3 same-department peers per employee · AI synthesis via masked gateway · rating anchored KPI 50% / targets 30% / peers 20%.
        </p>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-cycle-btn" className="rounded-none bg-[var(--brand-primary)] hover:bg-[#0f172a] h-9 text-[12px]">
                <Plus className="h-4 w-4" /> New Review Cycle
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md">
              <DialogHeader><DialogTitle className="text-[15px]">Open a review cycle</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Field label="Cycle name">
                  <Input data-testid="cycle-name-input" value={form.name} placeholder="H2 2026 360 Review"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Period start">
                    <Input data-testid="cycle-start-input" type="date" value={form.period_start}
                      onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="rounded-none" />
                  </Field>
                  <Field label="Period end">
                    <Input data-testid="cycle-end-input" type="date" value={form.period_end}
                      onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="rounded-none" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Scope">
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, department: v === "all" ? null : form.department })}>
                      <SelectTrigger className="rounded-none" data-testid="cycle-scope-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="all" className="text-[12px]">Whole company</SelectItem>
                        <SelectItem value="department" className="text-[12px]">Single department</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {form.scope === "department" && (
                    <Field label="Department">
                      <Select value={form.department || ""} onValueChange={(v) => setForm({ ...form, department: v })}>
                        <SelectTrigger className="rounded-none" data-testid="cycle-dept-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          {departments.map((d) => <SelectItem key={d} value={d} className="text-[12px]">{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="cycle-create-submit" onClick={create}
                  disabled={creating || !form.name || !form.period_start || !form.period_end}
                  className="rounded-none bg-teal-600 hover:bg-teal-700">
                  {creating ? "Opening…" : "Open cycle"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-border divide-y divide-border">
        {cycles.map((c) => {
          const counts = c.review_counts || {};
          const finalized = counts.finalized || 0;
          const pct = c.review_total ? Math.round((finalized / c.review_total) * 100) : 0;
          return (
            <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`cycle-row-${c.id}`}
              className="w-full flex items-center gap-5 px-5 py-4 text-left hover:bg-slate-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <p className="text-[14px] font-semibold text-[var(--brand-primary)]">{c.name}</p>
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 uppercase ${c.status === "active" ? "bg-teal-600 text-white" : "border border-slate-400 text-slate-600"}`}>
                    {c.status}
                  </span>
                </div>
                <p className="text-[11px] font-mono text-muted-foreground mt-1">
                  {c.period_start} → {c.period_end} · {c.scope === "all" ? "whole company" : c.department}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {Object.entries(counts).map(([s, n]) => (
                  <span key={s} className={`text-[9px] font-mono px-1.5 py-0.5 uppercase ${REVIEW_STATUS_CHIP[s]}`}>
                    {n} {s.replace("_", " ")}
                  </span>
                ))}
              </div>
              <div className="w-40 shrink-0">
                <div className="flex justify-between text-[10px] font-mono text-slate-500 mb-1">
                  <span>finalized</span><span>{pct}%</span>
                </div>
                <div className="h-1.5 bg-slate-100"><div className="h-1.5 bg-teal-600" style={{ width: `${pct}%` }} /></div>
              </div>
              <span className="text-[11px] text-teal-700 font-medium shrink-0">Open →</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CyclesPanel;
